import {Command, ContentDataCommand} from './commands';

import {PromiseQueue} from 'rlink-locking/promise-queue';
import {AsyncIterator} from 'rlink-utils/async';
import {EventEmitter} from 'rlink-utils/events';

const DEBUG_PROMISE_QUEUE_OPS=false;

export const STRUCTURED_DATA_PATH_PATTERN = /\.(json)$/


global.$repos = global.$repos || {};


export class BaseRepository extends EventEmitter {

  constructor ({contentRegistry}) {
    super();
    this.contentRegistry = contentRegistry;
    this.queue = new PromiseQueue();
    this.handleHistory = new WeakSet();

    if (this.constructor.name in global.$repos) {
      if (!Array.isArray(global.$repos[this.constructor.name])) {
        global.$repos[this.constructor.name] = [global.$repos[this.constructor.name]];
     }
      global.$repos[this.constructor.name].push(this);
    } else {
      global.$repos[this.constructor.name] = this;
    }

  }

  /**
   * @async
   * @param  {Command} command
   * @return {Promise}
   */
  exec (command) {
    let promise = this.queue.writeOp(
      () => this.handleCommand(command)
    );
    if(DEBUG_PROMISE_QUEUE_OPS) {
      command._opid = promise._opid;
      console.log('op#', promise._opid, 'is', command)
    }
    promise.then(() => this.emit('exec', command));
    return promise;
  }

  commandAlreadyHandled (command) {
    return this.handleHistory.has(command);
  }

  markCommandHandled (command) {
    this.handleHistory.add(command);
  }

  static isStructuredDataPath (path) {
    return STRUCTURED_DATA_PATH_PATTERN.test(path);
  }

  isStructuredDataPath (path) {
    return this.constructor.isStructuredDataPath(path);
  }

  // Note: afterEach only called if command persisted one by one!
  async execBatch (commands, afterEach) {
    for (var i = 0; i < commands.length; i++) {
      await this.exec(commands[i]);
      if (afterEach) {
        afterEach(commands[i], i);
      }
    }
    this.emit('execBatch');
  }

  async fetchContentAtPath (path, klass=null) {
    if (!path) {
      throw new Error('No path given for fetchContentAtPath');
    }
    let [data, metadata] = await this.fetchDataAtPath(path);
    if (!data) return;
    if (klass === null) {
      if (!metadata) {
        throw new Error(`No metadata available for content @ ${path}`);
      }
      if ( !('registeredClassName' in metadata) ) {
        throw new Error(`No registeredClassName for content @ ${path}. Metadata available: ${metadata}`);
      }
      klass = await this.loadContentClass(metadata.registeredClassName);
    }
    return klass.instantiateFromData(metadata || path, data);
  }

  // should return both data and metadata!
  fetchDataAtPath (path) {
    if (!path) {
      throw new Error('No path given for fetchDataAtPath');
    }
    if ('queuedFetchDataAndMetadata' in this) {
      return this.queue.readOp(
        () => this.queuedFetchDataAndMetadata(path).then(
          ([data, meta]) => [data, meta ? meta.withPath(path) : meta])
      );
    } else {
      return this.queue.readOp(
        () => Promise.all([
                this.queuedFetchData(path),
                this.queuedFetchMetadata(path)
                  .then(meta => meta ? meta.withPath(path) : meta)
              ])
      );
    }
  }


   /**
   * @param  {string} path
   * @return {Promise(Metadata)}
   */
  fetchMetadataAtPath (path) {
    if (!path) {
      throw new Error('No path given for fetchMetadataAtPath');
    }
    if ('queuedFetchMetadata' in this) {
      return this.queue.readOp(
        () => this.queuedFetchMetadata(path)
                  .then(meta =>  meta ? meta.withPath(path) : meta)
      );
    } else {
      return this.queue.readOp(
        () => this.queuedFetchDataAndMetadata(path)
              .then(([data, meta]) => meta ? meta.withPath(path) : meta)
      );
    }
  }

  async loadContentClass (className) {
    return await this.contentRegistry.loadClassNamed(
      className);
  }



  /**
   * Calls the given callback with each normalized path in the repo.
   *
   * The order of iteration is not defined and not guaranteed to be consistent
   * between calls.
   *
   * Returns a promise that resolves with a list of values returned by your
   * callback, or rejects with any value you throw.
   */
  mapPaths (prefix, callback) {
    return this.queue.readOp(
        () => this.queuedMapPaths(prefix, callback)
    );
  }

  /**
   * Returns the promise of an iterator which in turn yields a promise
   * for each path in the repository.
   *
   * @example
   * for (let promise of await this.eachPath('/')) {
   *   let path = await promise;
   * }
   *
   */
  eachPath (prefix, valueTransform=(a=>a)) {
    let iterator = new AsyncIterator();
    this.mapPaths(prefix, (path) => {
      iterator.provideValue(valueTransform(path));
    }).then(()=>{
      iterator.finish();
    }).catch((e)=>{
      iterator.error(e);
    });
    return iterator.iteratorPromise();
  }


  queuedMapPaths (prefix, callback) {
    throw new Error(`implement me! (${this.constructor.name}.queuedMapPaths())`);
  }

  /**
   * Calls the given callback with each content item in the repo.
   *
   * The order of iteration is not defined (as with mapPaths).
   *
   * Returns a promise that resolves when the iteration finished (or an iterator)
   *
   */
  eachContent (prefix, callback) {
    let transform = (path) => this.fetchContentAtPath(path);

    if (callback) {
      return this.mapPaths(prefix, (path) => transform(path).then(callback))
    } else {
      return this.eachPath(prefix, transform);
    }
  }

  /**
   * As above, but for [data, meta].
   */
  eachData (prefix, callback) {
    let transform = (path) => this.fetchDataAtPath(path);

    if (callback) {
      return this.mapPaths(prefix, (path) => transform(path).then(callback))
    } else {
      return this.eachPath(prefix, transform);
    }
  }
  /**
   * As above, but only for meta
   */
  eachMeta (prefix, callback) {
    let transform = (path) => this.fetchMetadataAtPath(path);

    if (callback) {
      return this.mapPaths(prefix, (path) => transform(path).then(callback))
    } else {
      return this.eachPath(prefix, transform);
    }
  }


  inspect () {
    return this.toString();
  }



  async handleCommand (command) {
    if ( !(command.constructor.isCommand) ) {
      throw new Error(`${this}: ${command} is not a Command!`);
    }
    if ( this.commandAlreadyHandled(command) ) {
      return;
    }
    let commandHandlerMethod = `handle${command.name}`;
    if (commandHandlerMethod in this) {
      this.markCommandHandled(command);

      await this[commandHandlerMethod](command);

    } else {
      let dataCommandHandlerMethod = 'handleContentDataCommand';
      if (command.constructor.isContentDataCommand
          && dataCommandHandlerMethod in this) {
        this.markCommandHandled(command);
        await this[dataCommandHandlerMethod](command);
      } else {
        throw new Error(`${this} does not implement ${commandHandlerMethod}()`);
      }
    }
  }



  async submit(...commandsOrContent) {
    //could try Immutable.List but Array#push really should be pretty fast
    let commandBatch = [];
    for( let commandOrContent of commandsOrContent) {
      if (!commandOrContent) {
        // discard blank commands
      } else if ('eachChange' in commandOrContent) {
        for(let command of commandOrContent.eachChange()) {
          commandBatch.push(command);
        }
      } else {
        // must be a command
        commandBatch.push(commandOrContent);
      }
    }
    // console.log(this.toString(), 'submitting->')
    // for( let cmd of commandBatch) {
    //   console.log(' -- ', cmd)
    // }
    await this.execBatch(commandBatch);
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-repos/base.js